import { doc, getDoc, collection, query, where, getDocs, DocumentData, getFirestore } from "firebase/firestore";
import { child, get, ref as dbRef } from "firebase/database";
import { defineStore } from "pinia";
import type { IUser} from "~~/types/user";
import { PermissionType } from "~~/types/user";
import Geolib from "geolib";
import type { ICase } from "~~/types/case";
import type { ICoord } from "~~/types/information";
import { userConverter } from "~~/converters/user-converter";

export interface NearbyBase {
  base: ICoord[];
  colorHex: string;
  contactNum: string;
  coord: ICoord;
  distanceFromOrigin: number;
  id: string;
  name: string;
  state: string[];
  type: string;
  regNum: string;
}

interface NearbyProvider {
  createdAt: {
    seconds: number;
    nanoseconds: number;
  };
  deviceId: string;
  contactNum: string;
  company: {
    type: string;
    name: string;
    id: string;
    base: Array<{
      latitude: number;
      longitude: number;
    }>;
    state: string[];
    contactNum: string;
    regNum: string;
  };
  priority: number;
  id: string;
  accountType: string;
  address: string;
  disabled: boolean;
  providerData: {
    leader: boolean;
    vehicle: any[];
    regNum: string;
  };
  email: string;
  photoUrl: string;
  permissions: string[];
  displayName: string;
  state: string[];
  coord: {
    latitude: string;
    longitude: string;
  };
  distanceFromOrigin: number;
}

const useUserStore = defineStore(`user`, () => {
  const caseStore = useCaseStore();
  const firestore = getFirestore();
  const authCurrentUser = useCurrentUser();
  const currentUser = ref<IUser | null>(null);
  const realtimeDB = useDatabase();

  /** `getRouteAuth` is specifically created for auth middleware where user credentials need to be fetched immediately with latest data */
  const getRouteAuth = async () => {
    const authCurrentUser = await getCurrentUser();

    if (!authCurrentUser) {
      return {
        user: null,
        allowedRoutes: getAllowedRoutes(),
      };
    }

    // if user is already in the store, return it without querying again since the firebase user is reactive and will be updated automatically
    if (currentUser.value) {
      return {
        user: currentUser.value,
        allowedRoutes: getAllowedRoutes(currentUser.value?.permissions, currentUser.value),
      };
    }

    try {
      const userQuery = doc(firestore, `users/${authCurrentUser.uid}`).withConverter(userConverter);
      const userDoc = await getDoc(userQuery);
      const user = userDoc.data() as IUser;

      currentUser.value = user;

      return {
        user,
        allowedRoutes: getAllowedRoutes(user?.permissions, user),
      };
    } catch (err: unknown) {
      // stale firebase auth after server session expired — firestore rejects the read.
      // sign out firebase so client state matches server and redirect to /login cleanly
      const code = (err as { code?: string })?.code ?? "";
      if (code === "permission-denied" || code === "unauthenticated") {
        await useAuthStore().forceSignOut();
        return { user: null, allowedRoutes: getAllowedRoutes() };
      }
      throw err;
    }
  };

  const firebaseCurrentUser = useDocument<IUser>(
    computed(() => {
      if (!authCurrentUser.value) return null;

      return doc(firestore, `users/${authCurrentUser.value.uid}`).withConverter(userConverter);
    })
  );

  watchEffect(() => {
    if (firebaseCurrentUser.value) {
      currentUser.value = firebaseCurrentUser.value;
    }
  });

  const allowedRoutes = computed(() => {
    return getAllowedRoutes(currentUser.value?.permissions, currentUser.value || undefined);
  });

  const getAllProviders = async ({
    states,
    coord,
  }: {
    states: string[];
    coord: ICase["location"]["from"]["coord"];
  }) => {
    if (!states || states.length <= 0 || !coord) {
      throw new Error(`Missing required state & coord parameters`);
    }

    if (!currentUser.value) {
      throw new Error(`Current user is missing`);
    }

    if (!caseStore?.selectedCase?.location.from.coord) {
      throw new Error(`Selected case OR its location is missing`);
    }

    const network = useNetwork();
    const caseLocationCoord = caseStore.selectedCase.location.from.coord;

    const [basesRes, providersRes, leadersRes] = await Promise.all([
      network.getAllBases({
        body: {
          states,
          caseLocationCoord,
        },
      }),
      network.getAllProviders({
        body: {
          states,
          caseLocationCoord,
        },
      }),
      network.getAllProvidersLeader({
        body: {
          states,
          caseLocationCoord,
        },
      }),
    ]);

    return {
      allBases: basesRes.data.allBases,
      allProviders: providersRes.data.allProviders,
      allProviderLeaders: leadersRes.data.allProviderLeaders,
    };
  };

  const getNearbyProvider = async ({
    states,
    coord,
    services,
  }: {
    states: string[];
    coord: ICase["location"]["from"]["coord"];
    services?: string[];
  }) => {
    if (!states || states.length <= 0 || !coord) {
      throw new Error(`Missing required state & coord parameters`);
    }

    if (!currentUser.value) {
      throw new Error(`Current user is missing`);
    }

    if (!caseStore?.selectedCase?.location.from.coord) {
      throw new Error(`Selected case OR its location is missing`);
    }

    const network = useNetwork();
    const caseLocationCoord = caseStore.selectedCase.location.from.coord;

    const [basesRes, providersRes, leadersRes] = await Promise.all([
      network.getNearbyBases({
        body: {
          states,
          caseLocationCoord,
        },
      }),
      network.getNearbyProvider({
        body: {
          caseLocationCoord,
          services,
        },
      }),
      network.getNearbyProviderLeader({
        body: {
          states,
          caseLocationCoord,
          services,
        },
      }),
    ]);

    return {
      nearbyBase: basesRes.data.nearbyBases,
      nearbyProvider: providersRes.data.nearbyProvider,
      nearbyProviderLeader: leadersRes.data.nearbyProviderLeaders,
    };
  };

  return {
    currentUser,
    allowedRoutes,
    getRouteAuth,
    getAllProviders,
    getNearbyProvider,
  };
});

export default useUserStore;
